import { WebCrawlerHeaderRedactedValue } from "@app/types/connectors/webcrawler";
import { hasRedactedHeaderValue } from "@app/types/shared/utils/http_headers";
import { Button, Input, XClose } from "@dust-tt/sparkle";
import { Trans, useLingui } from "@lingui/react/macro";
import { useFieldArray, useFormContext } from "react-hook-form";

type FormWithCustomHeaders = {
  customHeaders: Array<{ key: string; value: string }>;
  predefinedHeaderKeys?: string[];
};

export function MCPServerHeaders() {
  const { t } = useLingui();
  // `register` binds inputs via DOM refs so RHF tracks values natively without triggering React re-renders on each
  // keystroke. Using `update` instead would replace the field object (regenerating field.id), causing React to remount
  // the input on every keystroke and lose focus.
  const { control, register, getValues, watch } =
    useFormContext<FormWithCustomHeaders>();
  const predefinedHeaderKeys = watch("predefinedHeaderKeys");
  const hasRedactedValues = hasRedactedHeaderValue(
    watch("customHeaders") ?? []
  );
  const { fields, append, remove } = useFieldArray<
    FormWithCustomHeaders,
    "customHeaders"
  >({
    control,
    name: "customHeaders",
  });

  return (
    <div className="flex w-full flex-col">
      {hasRedactedValues && (
        <p className="mb-3 text-sm text-muted-foreground">
          <Trans>
            Header values are hidden once saved: re-enter every header value to
            update headers.
          </Trans>
        </p>
      )}
      <div className="flex flex-col gap-4">
        {fields.map((field, index) => {
          const isPredefined = predefinedHeaderKeys?.includes(field.key);
          const isRedacted =
            field.value === WebCrawlerHeaderRedactedValue ||
            getValues(`customHeaders.${index}.value`) ===
              WebCrawlerHeaderRedactedValue;
          return (
            <div key={field.id} className="flex items-center gap-2">
              <div className="grid flex-1 grid-cols-3 gap-2 px-1">
                <div className="col-span-1">
                  <Input
                    {...register(`customHeaders.${index}.key`)}
                    placeholder={t`Header name`}
                    disabled={isRedacted || isPredefined}
                    className="w-full"
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    {...register(`customHeaders.${index}.value`)}
                    placeholder={t`Header value`}
                    disabled={isRedacted}
                    className="w-full"
                  />
                </div>
              </div>
              {!isPredefined && (
                <Button
                  tooltip={t`Remove header`}
                  variant="outline"
                  icon={XClose}
                  onClick={() => remove(index)}
                />
              )}
            </div>
          );
        })}
      </div>
      <Button
        className="mt-4 self-start"
        variant="outline"
        label={t`Add header`}
        onClick={() => append({ key: "", value: "" })}
      />
    </div>
  );
}
